import { createElement } from '../../core/dom/helpers.js';

let nextId = 0;

/**
 * Compute avatar initials from a name string.
 * @param {string} name
 * @returns {string}
 */
function getInitials(name) {
  if (!name) return '?';
  const parts = name.split(' ').filter(Boolean);
  if (parts.length === 0) return '?';
  if (parts.length === 1) return parts[0][0].toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

/**
 * @param {HTMLElement} container
 * @param {{
 *   user: { name?: string, email?: string, roles?: string[] },
 *   onNavigate: (path: string) => void,
 *   onLogout: () => void,
 * }} options
 */
export function createUserMenu(container, { user, onNavigate, onLogout }) {
  const dropdownId = `user-menu-dropdown-${nextId++}`;

  // Harden user data
  const name = typeof user.name === 'string' && user.name.trim() ? user.name.trim() : '';
  const email = typeof user.email === 'string' ? user.email : '';
  const role = Array.isArray(user.roles) && user.roles.length > 0 ? String(user.roles[0]) : '';
  const initials = getInitials(name);

  // Build DOM
  const avatar = createElement('span', { class: 'user-menu-avatar' }, [initials]);
  const trigger = createElement('button', {
    class: 'user-menu-trigger',
    type: 'button',
    'aria-haspopup': 'true',
    'aria-expanded': 'false',
    'aria-label': 'User menu',
    'aria-controls': dropdownId,
  }, [avatar]);

  // Info section — only render non-empty lines
  const infoChildren = [];
  if (name) infoChildren.push(createElement('div', { class: 'user-menu-name' }, [name]));
  if (email) infoChildren.push(createElement('div', { class: 'user-menu-email' }, [email]));
  if (role) infoChildren.push(createElement('div', { class: 'user-menu-role' }, [role]));

  const dropdownChildren = [];
  if (infoChildren.length > 0) {
    const info = createElement('div', { class: 'user-menu-info' });
    for (const child of infoChildren) info.appendChild(child);
    dropdownChildren.push(info);

    const divider = document.createElement('hr');
    divider.className = 'user-menu-divider';
    dropdownChildren.push(divider);
  }

  dropdownChildren.push(
    createElement('button', {
      class: 'user-menu-item',
      type: 'button',
      'data-action': 'settings',
    }, ['Settings']),
  );
  dropdownChildren.push(
    createElement('button', {
      class: 'user-menu-item user-menu-item--danger',
      type: 'button',
      'data-action': 'logout',
    }, ['Log out']),
  );

  const dropdown = createElement('div', {
    class: 'user-menu-dropdown',
    id: dropdownId,
  });
  for (const child of dropdownChildren) dropdown.appendChild(child);

  const wrapper = createElement('div', { class: 'user-menu' });
  wrapper.appendChild(trigger);
  wrapper.appendChild(dropdown);
  container.appendChild(wrapper);

  // State
  let isOpen = false;
  /** @type {number} */
  let focusoutRafId = 0;

  // Handlers (stable references for add/removeEventListener)
  /** @param {PointerEvent} e */
  function onDocPointerdown(e) {
    if (!wrapper.contains(/** @type {Node} */ (e.target))) {
      close();
    }
  }

  /** @param {KeyboardEvent} e */
  function onDocKeydown(e) {
    if (e.key === 'Escape') {
      close(true);
    }
  }

  function open() {
    if (isOpen) return;
    isOpen = true;
    wrapper.classList.add('user-menu--open');
    trigger.setAttribute('aria-expanded', 'true');
    document.addEventListener('pointerdown', onDocPointerdown, { capture: true });
    document.addEventListener('keydown', onDocKeydown);
  }

  /**
   * @param {boolean} [returnFocus]
   */
  function close(returnFocus) {
    if (!isOpen) return;
    isOpen = false;
    wrapper.classList.remove('user-menu--open');
    trigger.setAttribute('aria-expanded', 'false');
    document.removeEventListener('pointerdown', onDocPointerdown, { capture: true });
    document.removeEventListener('keydown', onDocKeydown);
    cancelAnimationFrame(focusoutRafId);
    if (returnFocus && trigger.isConnected) {
      trigger.focus();
    }
  }

  // Trigger click — toggle
  function onTriggerClick() {
    if (isOpen) {
      close();
    } else {
      open();
    }
  }
  trigger.addEventListener('click', onTriggerClick);

  // Focusout — permanent, gated by isOpen
  function onFocusout() {
    if (!isOpen) return;
    focusoutRafId = requestAnimationFrame(() => {
      if (!isOpen) return;
      if (!wrapper.contains(document.activeElement)) {
        close();
      }
    });
  }
  wrapper.addEventListener('focusout', onFocusout);

  // Action clicks (delegated on wrapper)
  /** @param {MouseEvent} e */
  function onActionClick(e) {
    const target = /** @type {Element} */ (e.target);
    const actionEl = target.closest('[data-action]');
    if (!actionEl) return;
    const action = actionEl.getAttribute('data-action');
    if (action === 'settings') {
      close();
      onNavigate('/settings');
    } else if (action === 'logout') {
      close();
      onLogout();
    }
  }
  wrapper.addEventListener('click', onActionClick);

  function destroy() {
    close();
    trigger.removeEventListener('click', onTriggerClick);
    wrapper.removeEventListener('focusout', onFocusout);
    wrapper.removeEventListener('click', onActionClick);
    wrapper.remove();
  }

  return { destroy };
}
